import { useNavigate } from "react-router";
import { useAdminDashboard } from "../../api/admin";
import { formatDate, formatDateTime } from "../../utils/dateUtils";
import {
  BookStatusBadge,
  Card,
  PageHeader,
  StatCard,
  SubscriptionBadge,
} from "./ui";

export const AdminDashboard = () => {
  const { data, isLoading } = useAdminDashboard();
  const navigate = useNavigate();

  if (isLoading || !data) {
    return <div className="text-gray-500">Chargement…</div>;
  }

  const bookStatuses = Object.entries(data.books).filter(
    ([, count]) => count > 0,
  );

  return (
    <>
      <PageHeader
        title="Tableau de bord"
        subtitle="Vue d'ensemble de l'activité"
      />

      {(data.subscription_alerts.several_per_user > 0 ||
        data.subscription_alerts.several_books > 0) && (
        <button
          onClick={() => navigate("/admin/subscriptions")}
          className="w-full text-left mb-8 rounded-lg border border-red-200 bg-red-50 px-5 py-4 hover:bg-red-100"
        >
          <p className="text-sm font-semibold text-red-800">
            ⚠️ Abonnements à vérifier
          </p>
          <p className="text-sm text-red-700 mt-1">
            {data.subscription_alerts.several_per_user} compte(s) avec plusieurs
            abonnements · {data.subscription_alerts.several_books} ancien(s)
            achat(s) PDF en quantité. Ouvrir la page Abonnements pour réaffecter
            ou séparer.
          </p>
        </button>
      )}

      {data.stuck_books.length > 0 && (
        <Card
          title={`⚠️ Livres probablement bloqués (${data.stuck_books.length})`}
          className="mb-8 border-yellow-300"
        >
          <ul className="divide-y divide-gray-100">
            {data.stuck_books.map((book) => (
              <li
                key={book.id}
                onClick={() => navigate(`/admin/books/${book.id}`)}
                className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {book.title || `Livre #${book.id}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {book.user_email} · lancé le{" "}
                    {formatDateTime(book.updated_at)} ·{" "}
                    {book.reason === "crashed"
                      ? "la génération a planté (voir la page du livre)"
                      : `toujours en cours après ${book.stuck_after_minutes} min`}
                  </p>
                </div>
                <BookStatusBadge status={book.status} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Utilisateurs"
          value={data.users.total}
          hint={`${data.users.last_30_days} sur 30 jours`}
        />
        <StatCard
          label="Abonnements actifs"
          value={data.subscriptions.active}
          hint={`${data.subscriptions.pending} en attente`}
        />
        <StatCard
          label="Impressions à traiter"
          value={data.print_orders.pending}
          hint={`${data.print_orders.books_to_print} livres à imprimer`}
        />
        <StatCard
          label="Audiobooks"
          value={data.subscriptions.with_audiobook}
          hint="abonnements avec audio"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card title="Abonnements">
          <dl className="divide-y divide-gray-100 text-sm">
            {[
              ["Actifs", data.subscriptions.active],
              ["En attente", data.subscriptions.pending],
              ["Annulés", data.subscriptions.cancelled],
              ["Produit Print", data.subscriptions.print],
              ["Produit PDF", data.subscriptions.pdf],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between px-5 py-2.5">
                <dt className="text-gray-600">{label}</dt>
                <dd className="font-semibold text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card title="Livres par statut">
          <dl className="divide-y divide-gray-100 text-sm">
            {bookStatuses.length === 0 ? (
              <p className="px-5 py-3 text-gray-400">Aucun livre</p>
            ) : (
              bookStatuses.map(([status, count]) => (
                <div key={status} className="flex justify-between px-5 py-2.5">
                  <dt className="text-gray-600">{status}</dt>
                  <dd className="font-semibold text-gray-900">{count}</dd>
                </div>
              ))
            )}
          </dl>
        </Card>

        <Card title="Impressions">
          <dl className="divide-y divide-gray-100 text-sm">
            {[
              ["Total", data.print_orders.total],
              ["En attente", data.print_orders.pending],
              ["Confirmées", data.print_orders.confirmed],
              ["Livres à imprimer", data.print_orders.books_to_print],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between px-5 py-2.5">
                <dt className="text-gray-600">{label}</dt>
                <dd className="font-semibold text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      <Card title="Derniers inscrits">
        <ul className="divide-y divide-gray-100">
          {data.recent_users.map((user) => (
            <li
              key={user.id}
              onClick={() => navigate(`/admin/users/${user.id}`)}
              className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {user.full_name || user.email}
                </p>
                <p className="text-xs text-gray-500">
                  {user.email} · {formatDate(user.created_at)}
                </p>
              </div>
              <SubscriptionBadge status={user.subscription?.status} />
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
};
